import { useCallback } from "react";
import { useLocalStorage } from "./useLocalStorage";

const KEY = "voom_recent_searches";
const DEFAULT_MAX = 8;
const MIN_LENGTH = 2;

/**
 * Persistent recent search history. Stores up to `maxEntries` (default 8)
 * with case-insensitive dedup (latest wins, original casing preserved).
 *
 * Note: not reactive across components. SearchInput adding a search will not
 * push an update to a Products page already mounted; that page reads the list
 * once on mount. Acceptable for current UX.
 */
export function useRecentSearches(maxEntries: number = DEFAULT_MAX) {
  const [searches, setSearches] = useLocalStorage<string[]>(KEY, []);

  const add = useCallback(
    (raw: string) => {
      const trimmed = raw.trim();
      if (trimmed.length < MIN_LENGTH) return;
      setSearches(prev => {
        const filtered = prev.filter(
          s => s.toLowerCase() !== trimmed.toLowerCase()
        );
        return [trimmed, ...filtered].slice(0, maxEntries);
      });
    },
    [maxEntries, setSearches]
  );

  const clear = useCallback(() => setSearches([]), [setSearches]);

  return { searches, add, clear };
}
